import { defineStore } from 'pinia'
import {
  readPersistedStoreState,
  writePersistedStoreState,
} from '@/stores/persistence/pinia-indexeddb'
import {
  SHIPMENT_COMPOSER_DEFAULT_DWELL_MINUTES,
  SHIPMENT_COMPOSER_DWELL_MAX_MINUTES,
  SHIPMENT_COMPOSER_DWELL_MIN_MINUTES,
  SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_DEFAULT,
  SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MAX,
  SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MIN,
  SHIPMENT_COMPOSER_REST_HOURS_DEFAULT,
  SHIPMENT_COMPOSER_REST_HOURS_MAX,
  SHIPMENT_COMPOSER_REST_HOURS_MIN,
} from '@/components/shipment/form/utils/shipment-form-autofill-constants'

export type ShipmentFormConflictResolutionMode = 'origin' | 'destination'

const LEGACY_STORE_ID = 'shipmentWizardAutofillSettings'
const STORE_ID = 'shipmentComposerAutofillSettings'

let hydratedFromBrowserDb = false

function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min
  return Math.min(max, Math.max(min, Math.round(n)))
}

function clampHours(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min
  const r = Math.round(n * 10) / 10
  return Math.min(max, Math.max(min, r))
}

type PersistedAutofillSettings = {
  helpFillEnabled?: boolean
  showConflictWarnings?: boolean
  dwellMinutes?: number
  maxDrivingHours?: number
  restHours?: number
  lastConflictResolutionMode?: ShipmentFormConflictResolutionMode
}

function patchFromPayload(
  payload: PersistedAutofillSettings,
): PersistedAutofillSettings {
  return {
    helpFillEnabled: payload.helpFillEnabled ?? false,
    showConflictWarnings: payload.showConflictWarnings ?? true,
    dwellMinutes: clampInt(
      payload.dwellMinutes ?? SHIPMENT_COMPOSER_DEFAULT_DWELL_MINUTES,
      SHIPMENT_COMPOSER_DWELL_MIN_MINUTES,
      SHIPMENT_COMPOSER_DWELL_MAX_MINUTES,
    ),
    maxDrivingHours: clampHours(
      payload.maxDrivingHours ?? SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_DEFAULT,
      SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MIN,
      SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MAX,
    ),
    restHours: clampHours(
      payload.restHours ?? SHIPMENT_COMPOSER_REST_HOURS_DEFAULT,
      SHIPMENT_COMPOSER_REST_HOURS_MIN,
      SHIPMENT_COMPOSER_REST_HOURS_MAX,
    ),
    lastConflictResolutionMode:
      payload.lastConflictResolutionMode === 'destination'
        ? 'destination'
        : 'origin',
  }
}

export const useShipmentFormAutofillSettingsStore = defineStore(STORE_ID, {
  state: () => ({
    helpFillEnabled: false,
    showConflictWarnings: true,
    dwellMinutes: SHIPMENT_COMPOSER_DEFAULT_DWELL_MINUTES,
    maxDrivingHours: SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_DEFAULT,
    restHours: SHIPMENT_COMPOSER_REST_HOURS_DEFAULT,
    lastConflictResolutionMode: 'origin' as ShipmentFormConflictResolutionMode,
  }),
  getters: {
    dwellMs(): number {
      return (
        clampInt(
          this.dwellMinutes,
          SHIPMENT_COMPOSER_DWELL_MIN_MINUTES,
          SHIPMENT_COMPOSER_DWELL_MAX_MINUTES,
        ) *
        60 *
        1000
      )
    },
    maxDrivingMs(): number {
      return (
        clampHours(
          this.maxDrivingHours,
          SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MIN,
          SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MAX,
        ) *
        60 *
        60 *
        1000
      )
    },
    restMs(): number {
      return (
        clampHours(
          this.restHours,
          SHIPMENT_COMPOSER_REST_HOURS_MIN,
          SHIPMENT_COMPOSER_REST_HOURS_MAX,
        ) *
        60 *
        60 *
        1000
      )
    },
  },
  actions: {
    async hydrateFromBrowserDb() {
      if (hydratedFromBrowserDb) return
      let payload = await readPersistedStoreState<PersistedAutofillSettings>(STORE_ID)
      if (!payload) {
        payload = await readPersistedStoreState<PersistedAutofillSettings>(LEGACY_STORE_ID)
        if (payload) {
          void writePersistedStoreState(STORE_ID, payload)
        }
      }
      if (payload) {
        this.$patch(patchFromPayload(payload))
      }
      hydratedFromBrowserDb = true
    },
    persistToBrowserDb() {
      void writePersistedStoreState(STORE_ID, {
        helpFillEnabled: this.helpFillEnabled,
        showConflictWarnings: this.showConflictWarnings,
        dwellMinutes: clampInt(
          this.dwellMinutes,
          SHIPMENT_COMPOSER_DWELL_MIN_MINUTES,
          SHIPMENT_COMPOSER_DWELL_MAX_MINUTES,
        ),
        maxDrivingHours: clampHours(
          this.maxDrivingHours,
          SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MIN,
          SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MAX,
        ),
        restHours: clampHours(
          this.restHours,
          SHIPMENT_COMPOSER_REST_HOURS_MIN,
          SHIPMENT_COMPOSER_REST_HOURS_MAX,
        ),
        lastConflictResolutionMode: this.lastConflictResolutionMode,
      })
    },
    setHelpFillEnabled(v: boolean) {
      this.helpFillEnabled = v
      this.persistToBrowserDb()
    },
    setShowConflictWarnings(v: boolean) {
      this.showConflictWarnings = v
      this.persistToBrowserDb()
    },
    setDwellMinutes(v: number) {
      this.dwellMinutes = clampInt(
        v,
        SHIPMENT_COMPOSER_DWELL_MIN_MINUTES,
        SHIPMENT_COMPOSER_DWELL_MAX_MINUTES,
      )
      this.persistToBrowserDb()
    },
    setMaxDrivingHours(v: number) {
      this.maxDrivingHours = clampHours(
        v,
        SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MIN,
        SHIPMENT_COMPOSER_MAX_DRIVING_HOURS_MAX,
      )
      this.persistToBrowserDb()
    },
    setRestHours(v: number) {
      this.restHours = clampHours(
        v,
        SHIPMENT_COMPOSER_REST_HOURS_MIN,
        SHIPMENT_COMPOSER_REST_HOURS_MAX,
      )
      this.persistToBrowserDb()
    },
    setLastConflictResolutionMode(m: ShipmentFormConflictResolutionMode) {
      this.lastConflictResolutionMode = m
      this.persistToBrowserDb()
    },
  },
})
